import { DEFAULT_LOCALE, isLocale, type Locale } from "@/lib/i18n/config";

/** Marketing paths with honest locale-prefixed Spanish routes. */
export const LOCALIZED_ROUTE_KEYS = [
  "/",
  "/about",
  "/partners",
  "/investors",
  "/press",
  "/faq",
  "/connectors/claude",
  "/news",
  "/promociones/fundador",
] as const;

export type LocalizedRouteKey = (typeof LOCALIZED_ROUTE_KEYS)[number];

/** Legal hub + policy slugs (EN unprefixed, ES under `/es/legal/...`). */
export const LEGAL_ROUTE_PATHS = [
  "/legal",
  "/legal/privacy-policy",
  "/legal/terms-of-service",
  "/legal/cookie-policy",
  "/legal/do-not-sell",
  "/legal/legal-notice",
  "/legal/impressum",
  "/legal/accessibility-statement",
  "/legal/return-policy",
  "/legal/shipping-policy",
  "/legal/eula",
  "/legal/disclaimer",
  "/legal/acceptable-use-policy",
  "/legal/dpa",
  "/legal/service-level-agreement",
] as const;

export type LegalRoutePath = (typeof LEGAL_ROUTE_PATHS)[number];

function newsPathSuffix(path: string): string | null {
  if (path === "/news" || path.startsWith("/news/")) return path;
  const match = path.match(/^\/([A-Za-z-]+)(\/news(?:\/.*)?)$/);
  if (!match || !isLocale(match[1]) || match[1] === "en") return null;
  return match[2];
}

export function stripLocalePrefix(pathname: string): string {
  const path = pathname.split("?")[0].split("#")[0] || "/";
  const news = newsPathSuffix(path);
  if (news && news !== path) return news;
  if (path === "/es" || path.startsWith("/es/")) {
    const rest = path.slice(3) || "/";
    return rest.startsWith("/") ? rest : `/${rest}`;
  }
  return path.startsWith("/") ? path : `/${path}`;
}

/** Locale encoded in the URL prefix, or null when the path is unprefixed. */
export function localePrefixFromPathname(pathname: string): Locale | null {
  const path = pathname.split("?")[0].split("#")[0] || "/";
  const prefixedNews = path.match(/^\/([A-Za-z-]+)\/news(?:\/|$)/);
  if (prefixedNews && isLocale(prefixedNews[1]) && prefixedNews[1] !== "en") {
    return prefixedNews[1];
  }
  if (path === "/es" || path.startsWith("/es/")) return "es";
  return null;
}

export function localeFromPathname(pathname: string): Locale {
  return localePrefixFromPathname(pathname) ?? DEFAULT_LOCALE;
}

export function isLocalizedRouteKey(path: string): path is LocalizedRouteKey {
  const key = (path === "" ? "/" : path) as LocalizedRouteKey;
  return (LOCALIZED_ROUTE_KEYS as readonly string[]).includes(key);
}

/** Paths that encode locale in the URL (marketing + legal). */
export function isLocaleAwarePath(path: string): boolean {
  const normalized = path === "" ? "/" : path;
  if (isLocalizedRouteKey(normalized)) return true;
  return normalized === "/legal" || normalized.startsWith("/legal/");
}

/** Build the public path for a locale (EN unprefixed, ES under `/es`). */
export function localizePath(path: string, locale: Locale): string {
  const [rawPath, hash = ""] = path.split("#");
  const [pathname, search = ""] = rawPath.split("?");
  const key = stripLocalePrefix(pathname || "/");
  const normalized = key === "" ? "/" : key;

  let localized: string;
  const newsPath = normalized === "/news" || normalized.startsWith("/news/");
  if (newsPath && locale !== "en") {
    localized = `/${locale}${normalized}`;
  } else if (locale === "es" && isLocaleAwarePath(normalized)) {
    localized = normalized === "/" ? "/es" : `/es${normalized}`;
  } else if (locale === "es") {
    // Truly shared routes (no locale prefix) stay as-is.
    localized = normalized;
  } else {
    localized = normalized === "/" ? "/" : normalized;
  }

  const query = search ? `?${search}` : "";
  const fragment = hash ? `#${hash}` : "";
  return `${localized}${query}${fragment}`;
}

export function swapLocalePath(pathname: string, nextLocale: Locale): string {
  const key = stripLocalePrefix(pathname);
  return localizePath(key, nextLocale);
}

export function parseLocaleParam(value: string | undefined | null): Locale {
  return isLocale(value) ? value : DEFAULT_LOCALE;
}
